"use client";

import type { FormEvent } from "react";
import { useState } from "react";
import { quoteMessage, siteConfig } from "@latam-energy/content";
import { AppLink as Link } from "./AppLink";
import { trackEvent } from "@/lib/analytics";
import { sendFormEmail } from "@/lib/email";

type FormKind = "contacto" | "cotizacion" | "newsletter";

export function ContactForm({ kind = "contacto" }: { kind?: Extract<FormKind, "contacto" | "cotizacion"> }) {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [message, setMessage] = useState("");

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    setStatus("loading");
    setMessage("");

    try {
      const formData = new FormData(form);
      const payload = {
        name: formData.get("name") as string,
        email: formData.get("email") as string,
        company: formData.get("company") as string,
        position: formData.get("position") as string,
        phone: formData.get("phone") as string,
        country: formData.get("country") as string,
        city: formData.get("city") as string,
        interest: formData.get("interest") as string,
        quantity: formData.get("quantity") as string,
        message: formData.get("message") as string,
        projectDescription: formData.get("projectDescription") as string,
        preferredContact: (formData.get("preferredContact") as "email" | "telefono" | "whatsapp") || "email",
        formType: kind,
      };

      const result = await sendFormEmail(payload);

      if (result.success) {
        setStatus("success");
        setMessage(result.message);
        trackEvent(kind === "cotizacion" ? "quotation_form_submit" : "contact_form_submit");
        form.reset();
        
        // Auto-clear success message after 5 seconds
        setTimeout(() => {
          setMessage("");
          setStatus("idle");
        }, 5000);
      } else {
        setStatus("error");
        setMessage(result.message || "Error al enviar la solicitud. Por favor intenta de nuevo.");
      }
    } catch (error) {
      console.error("Form submission error:", error);
      setStatus("error");
      setMessage("Error al procesar tu solicitud. Por favor intenta de nuevo.");
    }
  }

  return (
    <form className="card grid gap-4 p-6" onSubmit={onSubmit} noValidate>
      <div className="grid gap-4 md:grid-cols-2">
        <Field name="name" label="Nombre" autoComplete="name" required />
        <Field name="company" label="Empresa" autoComplete="organization" required />
        <Field name="position" label="Cargo" autoComplete="organization-title" />
        <Field name="email" label="Correo" type="email" autoComplete="email" required />
        <Field name="phone" label="Teléfono" type="tel" autoComplete="tel" required />
        <Field name="country" label="País" autoComplete="country-name" required />
        <Field name="city" label="Ciudad" autoComplete="address-level2" required />
        <Field name="interest" label="Servicio o producto de interés" required />
      </div>
      {kind === "cotizacion" ? (
        <div className="grid gap-4 md:grid-cols-2">
          <Field name="quantity" label="Cantidad estimada" />
          <label className="grid gap-2 text-sm font-bold text-steel">
            Método de contacto preferido
            <select className="rounded-md border border-slate-300 p-3" name="preferredContact" defaultValue="whatsapp">
              <option value="whatsapp">WhatsApp</option>
              <option value="email">Correo</option>
              <option value="telefono">Teléfono</option>
            </select>
          </label>
        </div>
      ) : (
        <input type="hidden" name="preferredContact" value="email" />
      )}
      <label className="grid gap-2 text-sm font-bold text-steel">
        Mensaje
        <textarea className="min-h-32 rounded-md border border-slate-300 p-3" name="message" required />
      </label>
      {kind === "cotizacion" ? (
        <label className="grid gap-2 text-sm font-bold text-steel">
          Descripción del proyecto
          <textarea className="min-h-28 rounded-md border border-slate-300 p-3" name="projectDescription" />
        </label>
      ) : null}
      <label className="hidden">
        Website
        <input name="website" tabIndex={-1} autoComplete="off" />
      </label>
      <label className="flex items-start gap-3 text-sm text-slate-700">
        <input className="mt-1" type="checkbox" name="privacyConsent" required />
        <span>Acepto la <Link className="font-bold text-steel underline" href="/politica-de-privacidad">política de privacidad</Link> y el tratamiento de mis datos para responder esta solicitud.</span>
      </label>
      {message ? <p className={status === "error" ? "rounded-md bg-red-50 p-3 text-red-700" : "rounded-md bg-emerald-50 p-3 text-emerald-700"} role="status">{message}</p> : null}
      <button className="btn btn-primary w-fit" disabled={status === "loading"} type="submit">{status === "loading" ? "Enviando..." : kind === "cotizacion" ? "Enviar solicitud de cotización" : "Enviar mensaje"}</button>
    </form>
  );
}

export function NewsletterForm() {
  const [status, setStatus] = useState("");
  return (
    <form
      className="grid gap-3 md:grid-cols-[1fr_auto]"
      onSubmit={(event) => {
        event.preventDefault();
        setStatus("Gracias. Te contactaremos cuando el boletín técnico esté activo.");
        trackEvent("newsletter_subscription");
        event.currentTarget.reset();
      }}
    >
      <label className="sr-only" htmlFor="newsletter-email">Correo electronico</label>
      <input id="newsletter-email" required type="email" name="email" autoComplete="email" placeholder="correo@empresa.com" className="min-h-12 rounded-md border border-slate-300 p-3" />
      <button className="btn btn-primary" type="submit">Suscribirme</button>
      <label className="md:col-span-2 flex items-start gap-3 text-sm text-slate-600">
        <input className="mt-1" type="checkbox" required />
        <span>Acepto recibir información técnica y puedo solicitar la baja en cualquier momento.</span>
      </label>
      {status ? <p className="md:col-span-2 rounded-md bg-emerald-50 p-3 text-emerald-700" role="status">{status}</p> : null}
    </form>
  );
}

function Field({ name, label, type = "text", autoComplete, required = false }: { name: string; label: string; type?: string; autoComplete?: string; required?: boolean }) {
  return (
    <label className="grid gap-2 text-sm font-bold text-steel">
      {label}
      <input className="rounded-md border border-slate-300 p-3" name={name} type={type} autoComplete={autoComplete} required={required} />
    </label>
  );
}
